import { Analytics } from '@cobliteam/analytics';
import type { Driver } from '@cobliteam/api-lib';
import type { DriverFaceImageAssignRequest } from '@cobliteam/common-types';
import { useDashboard } from '@cobliteam/dashboard/src/providers/DashboardProvider';
import { useAssignDriverFaceImage } from '@cobliteam/hooks/src/useCamera/useDriverFaceCluster';
import useChatWidget from '@cobliteam/hooks/src/useChatWidget';
import useConfirmBeforeLeave from '@cobliteam/hooks/src/useConfirmBeforeLeave';
import useDriver, { QUERY_KEY } from '@cobliteam/hooks/src/useEntity/useDriver';
import useIdentifierAssociations from '@cobliteam/hooks/src/useEntity/useIdentifierAssociations';
import { useTranslation } from '@cobliteam/i18n';
import { usePermissions } from '@cobliteam/permissions';
import { Alert, Divider, Drawer, DrawerBody, DrawerContent, DrawerFooter } from '@cobliteam/ui/components';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Prompt } from 'react-router-dom';
import { AlertDialogCancel } from '../../AlertDialog';
import DeleteDriverConfirmModal from './components/DeleteDriverConfirmModal';
import DriverDrawerHeader from './components/DriverDrawerHeader';
import DriverFaceIdImagesSummary from './components/DriverFaceIdImagesSummary';
import FooterButtons from './components/FooterButtons';
import DriverDrawerSkeleton from './DriverDrawerSkeleton';
import DriverForm, { type DriverFormRef } from './DriverForm';
import DriverImagesUpload, { type DriverImagesRef, type StagedFaceImageUpdate } from './DriverImagesUpload';
import type { DriverDrawerStep, DriverDrawerType } from './types';

export interface DriverDrawerProps {
  open: boolean;
  driver?: Driver;
  allDriversData: Driver[];
  isLoading: boolean;
  initialStep?: DriverDrawerStep;
  onCloseDrawer: () => void;
  onSuccess?: (createdDriver: Driver) => void;
  onSuggestionsClick?: (driverId: string) => void;
}

const newDriver: Driver = {
  id: '',
  fleetId: '',
  name: '',
  phoneNumbers: [''],
  identifier: '',
  identificationToken: undefined,
  driverCode: undefined,
  licenseCategory: undefined,
  firstLicenseAcquirementDateInMillis: undefined,
  licenseExpirationDateInMillis: undefined,
};

const DriverDrawer = ({ open, driver = newDriver, onCloseDrawer, onSuccess, onSuggestionsClick, allDriversData, isLoading, initialStep }: DriverDrawerProps) => {
  const { setPosition } = useChatWidget();
  const queryClient = useQueryClient();
  const { t } = useTranslation('drivers');

  const { runtimeFlags } = useDashboard();
  const hasCamera = runtimeFlags.hasCameraFlag;

  const { isAccessAllowed } = usePermissions();
  const hasFaceIdPermission = isAccessAllowed('update:driver-face-cluster');
  const hasViewCameraMediaPermission = isAccessAllowed('view:camera-media');

  const { useDeleteDriver } = useDriver();
  const { mutate: deleteDriverHook, isPending: isDeletingDriver } = useDeleteDriver();

  const { useSaveDriver } = useDriver();
  const { mutateAsync: mutateSaveDriver, isPending: isSavingDriver } = useSaveDriver();

  const { mutateAsync: assignDriverFaceImage, isPending: isAssigningImages } = useAssignDriverFaceImage();

  const { associateTokenToDriver, disassociateTokenFromDriver } = useIdentifierAssociations.useIdentificationToken();
  const associateTokenToDriverMutation = associateTokenToDriver();
  const disassociateTokenFromDriverMutation = disassociateTokenFromDriver();

  const [openConfirmDeleteDialog, setOpenConfirmDeleteDialog] = useState(false);
  const [canSubmit, setCanSubmit] = useState(true);
  const [step, setStep] = useState<DriverDrawerStep>(initialStep ?? 'form');

  const [_driver, _setDriver] = useState(driver);
  const [driverImages, setDriverImages] = useState<StagedFaceImageUpdate | null>(null);
  const [showAlertDialogCancel, setShowAlertDialogCancel] = useState(false);

  const driverFormRef = useRef<DriverFormRef>(null);
  const driverImagesRef = useRef<DriverImagesRef>(null);

  const type: DriverDrawerType = driver.id ? 'edit' : 'add';
  const canAddImages = hasCamera && hasFaceIdPermission;
  const showFaceIdSummary = type === 'edit' && hasCamera && hasViewCameraMediaPermission;
  const isSaving = isSavingDriver || isAssigningImages;

  useEffect(() => {
    _setDriver(driver);

    if (open) {
      setDriverImages(null);
      setStep(initialStep ?? 'form');
    }
  }, [driver, open, initialStep]);

  useEffect(() => {
    setPosition(open ? 'drawer' : 'default');
  }, [open, setPosition]);

  const saveDriver = async (stagedDriver: Driver): Promise<Driver> => {
    Analytics.track(`Driver Drawer ${driver.id ? 'Update' : 'Save'} Button Clicked`);

    const savedDriver = await mutateSaveDriver(stagedDriver, {
      onSuccess: (driver) => onSuccess?.(driver),
    });

    const changedIdentificationToken = stagedDriver.identificationToken?.tokenId !== driver.identificationToken?.tokenId;

    if (changedIdentificationToken) {
      await handleSaveIdentificationToken(stagedDriver, savedDriver);
    }

    if (driver.identificationToken?.tokenAlias !== stagedDriver.identificationToken?.tokenAlias) {
      Analytics.track('Driver Drawer - Token Alias Changed', {
        driverId: savedDriver.id,
        oldTokenAlias: driver.identificationToken?.tokenAlias,
        newTokenAlias: stagedDriver.identificationToken?.tokenAlias,
      });
    }

    return savedDriver;
  };

  const handleSaveIdentificationToken = async (stagedDriver: Driver, savedDriver: Driver) => {
    if (stagedDriver.identificationToken?.tokenId === undefined && driver.identificationToken?.tokenId !== undefined) {
      disassociateTokenFromDriverMutation.mutate({ tokenId: driver.identificationToken.tokenId });
    } else {
      await associateTokenToDriverMutation.mutateAsync({
        driverId: savedDriver.id,
        tokenId: stagedDriver.identificationToken?.tokenId || '',
      });
    }
  };

  const onFormSubmit = () => {
    driverFormRef.current?.handleSubmit();
  };

  const onImagesSubmit = () => {
    driverImagesRef.current?.handleSubmit();
  };

  const onSave = async (driver: Driver, driverImages: StagedFaceImageUpdate | null, driverChanged: boolean) => {
    let driverId = _driver.id;

    if (driverChanged) {
      const savedDriver = await saveDriver(driver);
      driverId = savedDriver.id;
    }

    const imagesChanged = driverImages && (driverImages.changedAssignments.length > 0 || driverImages.removedAssignments.length > 0);

    if (imagesChanged) {
      const changedAssignments: DriverFaceImageAssignRequest[] = driverImages.changedAssignments.map((it) => ({
        driverId,
        url: it.url,
        useAsProfileImage: it.useAsProfileImage,
      }));

      const removedAssignments: DriverFaceImageAssignRequest[] = driverImages.removedAssignments.map((it) => ({
        driverId: null,
        url: it.url,
      }));

      await assignDriverFaceImage(changedAssignments.concat(removedAssignments));

      if (!driverChanged) Alert.success('Motorista salvo com sucesso.');
    }

    onCloseDrawer();
  };

  const onFormFinish = async (stagedDriver: Driver, hasChanges: boolean) => {
    // It handlers the form validated submitted driver. Saves and closes directly only when:
    // - Editing mode
    // - Adding without images step
    // Otherwise, stages the driver locally for latter saving.

    _setDriver(stagedDriver);

    if (!canAddImages || type === 'edit') {
      await onSave(stagedDriver, driverImages, hasChanges);
    } else {
      setStep('images');
    }
  };

  const onImagesFinish = async (imagesUpdates: StagedFaceImageUpdate) => {
    setDriverImages(imagesUpdates);

    if (type === 'add') {
      if (!_driver) return;
      await onSave(_driver, imagesUpdates, true); // On adding, driver surely has changed
    }

    if (type === 'edit') {
      setStep('form');
    }
  };

  const onClickDeleteDriver = () => setOpenConfirmDeleteDialog(true);

  const deleteDriver = () => {
    deleteDriverHook(driver.id, {
      onSuccess: () => {
        Analytics.track('Driver Drawer Delete Success');
        queryClient.resetQueries({ queryKey: [QUERY_KEY] });
      },
      onError: () => Analytics.track('Driver Drawer Delete Error'),
    });

    onCloseDrawer();
    setOpenConfirmDeleteDialog(false);
  };

  const handleDelete = (values: Driver) => {
    if (values.identificationToken?.tokenId) {
      disassociateTokenFromDriverMutation.mutate(
        {
          tokenId: values.identificationToken.tokenId,
        },
        {
          onSuccess: () => {
            deleteDriver();
          },
        }
      );
    } else {
      deleteDriver();
    }

    Analytics.track('Driver Drawer Delete Button Clicked');
  };

  const hasImageChanges = (): boolean => {
    // Check if we're on the images step and there are changes
    if (step === 'images') {
      return driverImagesRef.current?.hasChanges?.() ?? false;
    }

    // Check if there are staged changes from previous steps
    if (!driverImages) return false;
    return driverImages.changedAssignments.length > 0 || driverImages.removedAssignments.length > 0;
  };

  const hasUnsavedChanges = open && ((driverFormRef.current?.isDirty?.() ?? false) || hasImageChanges());

  const { handleBlockedNavigation } = useConfirmBeforeLeave({
    hasUnsavedChanges,
    onLeave: () => {
      onCloseDrawer();
    },
  });

  const requestCloseDrawer = () => {
    const formIsDirty = driverFormRef.current?.isDirty?.() ?? false;
    const imagesDirty = hasImageChanges();

    if (formIsDirty || imagesDirty) {
      setShowAlertDialogCancel(true);
      return;
    }

    onCloseDrawer();
  };

  const handleCancel = () => {
    requestCloseDrawer();
    Analytics.track('Driver Drawer Cancel Button Clicked');
  };

  const handleCloseDrawer = () => {
    requestCloseDrawer();
    Analytics.track('Driver Drawer Close Button Clicked');
  };

  const handleConfirmDiscard = () => {
    setShowAlertDialogCancel(false);
    onCloseDrawer();
  };

  const handleManageImages = () => {
    setStep('images');
    Analytics.track('Driver Drawer - Manage Images - Click');
  };

  return (
    <>
      <Prompt when={hasUnsavedChanges} message={handleBlockedNavigation} />
      <Drawer open={open} onClose={handleCloseDrawer}>
        <DrawerContent>
          <DriverDrawerHeader type={type} step={step} stepper={canAddImages} goToStep={setStep} initialStep={initialStep} />
          <DrawerBody className='overflow-auto'>
            {isLoading ? (
              <DriverDrawerSkeleton closeDrawer={onCloseDrawer} />
            ) : (
              <>
                <div className='w-full'>
                  {step === 'form' && (
                    <>
                      {showFaceIdSummary && hasCamera && (
                        <>
                          <DriverFaceIdImagesSummary
                            driverId={driver.id}
                            hasEditPermission={hasFaceIdPermission}
                            preview={driverImages?.previewUrls}
                            onButtonClick={handleManageImages}
                          />
                          <Divider className='my-5' />
                        </>
                      )}
                      <DriverForm
                        ref={driverFormRef}
                        driver={_driver}
                        allDriversData={allDriversData}
                        isLoading={isSavingDriver}
                        setCanSubmit={setCanSubmit}
                        onFinish={onFormFinish}
                      />
                    </>
                  )}
                  {step === 'images' && (
                    <DriverImagesUpload
                      ref={driverImagesRef}
                      driver={_driver}
                      stagedImagesUpdate={driverImages}
                      setCanSubmit={setCanSubmit}
                      onFinish={onImagesFinish}
                      onSuggestionsClick={onSuggestionsClick ? () => onSuggestionsClick(driver.id) : undefined}
                    />
                  )}
                </div>
                <DeleteDriverConfirmModal
                  open={openConfirmDeleteDialog}
                  loading={isDeletingDriver}
                  driverName={driver.name}
                  onCancel={() => setOpenConfirmDeleteDialog(false)}
                  onConfirm={() => handleDelete(driver)}
                />
              </>
            )}
          </DrawerBody>
          <DrawerFooter>
            <FooterButtons
              step={step}
              type={type}
              canSubmit={canSubmit}
              canAddImages={canAddImages}
              isLoading={isSaving}
              onFormDoneClick={onFormSubmit}
              onImagesDoneClick={onImagesSubmit}
              onCancelClickButton={handleCancel}
              onDeleteClickButton={onClickDeleteDriver}
              goToStep={setStep}
            />
          </DrawerFooter>
        </DrawerContent>

        <AlertDialogCancel
          open={showAlertDialogCancel}
          title={t('close_drawer_without_saving.title')}
          description={t('close_drawer_without_saving.description')}
          confirmLabel={t('close_drawer_without_saving.confirmLabel')}
          onOpenChange={setShowAlertDialogCancel}
          onConfirm={handleConfirmDiscard}
        />
      </Drawer>
    </>
  );
};

export default DriverDrawer;
